import { useState } from 'react'
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormGroup, Grid, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { trackActivity } from '../../../../utils/mixpanel';
import { regexConstants } from '../../../../constants/regEx';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment';
import { backendDateFormat, fieldDateFormat } from '../../../../constants';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { globalPostService, globalPutService } from '../../../../utils/globalApiServices';
import { checkApiStatus } from '../../../../utils';
import { addCalenderApi, updateCalenderApi } from '../apiService';


const STATUS_OPTIONS = [{ label: 'Active', value: 1 }, { label: 'De-Active', value: 2 }];

const CalenderCRU = ({ calenderCRU, modeType, getResponseBack, onToggleModal }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = useState({
        ...calenderCRU?.data,
        business_week_days: calenderCRU?.data?.business_week_days || { Mon: false, Tue: false, Wed: false, Thu: false, Fri: false, Sat: false, Sun: false }
    });
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState({});

    const onFieldChange = (keyParam, value) => {
        setData({ ...data, [keyParam]: value })
    }

    const onDayChange = (day, checked) => {
        setData(prev => ({
            ...prev,
            business_week_days: {
                ...prev.business_week_days,
                [day]: checked
            }
        }));
    };

    const onAddEditCalender = () => {
        let validationInputs = {
            name: data?.name?.trim() ? "" : "Please enter Name",
            effective_from: data?.effective_from ? "" : "Please enter Effective From",
            effective_till: data?.effective_till ? "" : "Please enter Effective Till",
            status: data?.status ? "" : "Please select Status"
        };

        const from = moment(data?.effective_from);
        const till = moment(data?.effective_till);

        if (data?.effective_from && data?.effective_till) {
            if (!from.isValid()) {
                validationInputs.effective_from = "Please enter valid Effective From";
            } else if (!till.isValid()) {
                validationInputs.effective_till = "Please enter valid Effective Till";
            } else if (till.isSame(from)) {
                validationInputs.effective_till = "Effective From and Effective Till cannot be the same date";
            } else if (till.isBefore(from)) {
                validationInputs.effective_till = "Effective Till cannot be before Effective From";
            }
        }

        if (!Object.values(validationInputs).every(v => v === "")) {
            setError(validationInputs);
            trackActivity(`Item ${modeType === 'add' ? 'Added' : 'Edited'}`, { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Calendar`, error_msg: validationInputs, })
            return;
        }

        const hasAnyDay = Object.values(data.business_week_days).some(Boolean);
        if (!hasAnyDay) {
            enqueueSnackbar(
                "Please select at least one Business Week Day",
                { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" } }
            );
            return;
        }

        const payload = {
            name: data?.name || null,
            status: data?.status,
            effective_from: data?.effective_from || null,
            effective_till: data?.effective_till || null,
            remarks: data?.remarks || null,
            business_week_days: data.business_week_days
        };

        setLoading(true);
        const apiCall = data?.id
            ? globalPutService(updateCalenderApi({ calender_slug: data?.slug }), payload)
            : globalPostService(addCalenderApi({}), payload);

        apiCall.then(response => {
            setLoading(false);
            if (checkApiStatus(response)) {
                enqueueSnackbar(response?.data?.message, { variant: "success" });
                onToggleModal();
                getResponseBack();
                trackActivity(data?.id ? 'Item Edited' : 'Item Added', {
                    page_title: 'Calendar',
                    payload
                });
            } else {
                enqueueSnackbar(response?.data?.message, { variant: "error" });
            }
        });
    };

    const onRestErrorKey = (keyParam) => {
        setError(prev => ({ ...prev, [keyParam]: '' }))
    }

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Calendar`, page_type: page_type, })
    }
    return (
        <>
            <Dialog aria-labelledby="customized-dialog-title" className="add-lease-dialog" maxWidth="md" open={true} fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, pl:3 }} className='dialog-header'>
                    {modeType === "add" ? "Add Calendar" : modeType === "edit" ? "Edit Calendar" : "View Calendar"}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ md: 6, xs: 12 }}>
                            <TextField
                                disabled={modeType === "view"}
                                id="name"
                                label="Name"
                                variant="outlined"
                                size="small"
                                margin="normal"
                                onChange={(e) => { regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange('name', e.target.value.trimStart()) : e.preventDefault() }}
                                onFocus={() => onRestErrorKey("name")}
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: {maxLength: 100} }}
                                value={data?.name || ''}
                                error={!!error.name}
                                helperText={error.name}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }}>
                            <Autocomplete
                                        disablePortal
                                        disableClearable
                                        options={STATUS_OPTIONS}
                                        disabled={modeType === 'view'}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        getOptionLabel={(option) => option.label || ""}
                                        value={data?.status || null}
                                        onChange={(_, value) => onFieldChange("status", value)}
                                        onFocus={() => onRestErrorKey("status")}
                                        renderInput={(params) => (
                                            <TextField
                                                required
                                                {...params}
                                                label="Status"
                                                margin='normal'
                                                size='small'
                                                error={Boolean(error.status)}
                                                helperText={error.status}
                                                slotProps={{ inputLabel: { shrink: true } }}
                                                disabled={modeType === 'view'}
                                            />
                                        )}
                                    />
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Effective From"
                                    value={data?.effective_from ? moment(data.effective_from) : null}
                                    onChange={(data) => {
                                        const isValid = moment(data)?.isValid();
                                        onFieldChange("effective_from", data ? moment(data).format(backendDateFormat) : null);
                                        if (isValid) {
                                            onRestErrorKey("effective_from");
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            InputLabelProps: { shrink: true },
                                            error: error.effective_from || false,
								            helperText: error.effective_from || '',
                                            size: "small",
                                            margin: "normal",
                                            onFocus: () => {
												if (error.date) {
													onRestErrorKey("effective_from");
												}
											},
                                        },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("effective_from", null);
                                                onRestErrorKey("effective_from");
                                            }
                                        }
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ md: 6, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Effective Till"
                                    value={data?.effective_till ? moment(data.effective_till) : null}
                                    onChange={(data) => {
                                        const isValid = moment(data)?.isValid();
                                        onFieldChange("effective_till", data ? moment(data).format(backendDateFormat) : null);
                                        if (isValid) {
                                            onRestErrorKey("effective_till");
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            InputLabelProps: { shrink: true },
                                            error: error.effective_till || false,
								            helperText: error.effective_till || '',
                                            size: "small",
                                            margin: "normal",
                                            onFocus: () => {
												if (error.date) {
													onRestErrorKey("effective_till");
												}
											},
                                        },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("effective_till", null);
                                                onRestErrorKey("effective_till");
                                            }
                                        }
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ md: 12, xs: 12 }}>
                            <TextField
                                disabled={modeType === "view"}
                                id="remarks"
                                label="Remarks"
                                variant="outlined"
                                size="small"
                                margin="normal"
                                slotProps={{ inputLabel: { shrink: true } }}
                                sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                                onChange={(e) => { regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange('remarks', e.target.value.trimStart()) : e.preventDefault() }}
                                value={data?.remarks || ''}
                                error={!!error.remarks}
                                helperText={error.remarks}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Grid>
                        <Grid size={{ md: 12, xs: 12 }}>
                            <Typography className='section-header-text' sx={{fontSize:"13px"}}>Business Week Day’s</Typography>
                            <FormGroup sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', ml: '-10px' }}>
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                                    <Box key={day} className="check-item-div">
                                        <Checkbox
                                            size="small"
                                            checked={Boolean(data?.business_week_days?.[day])}
                                            onChange={(e) => onDayChange(day, e.target.checked)}
                                            disabled={modeType === 'view'}
                                        />
                                        <span className="check-item-span file-name">{day}</span>
                                    </Box>
                                ))}
                            </FormGroup>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ marginRight: "15px" }}>
                    <Button className='cancel-btn' disabled={isLoading} variant="outlined" size='small' onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>{modeType === "view" ? "Close" : "Cancel"}</Button>
                    {modeType === "view" ? null :
                        <Button variant="contained" size='small' className="btn-primary" onClick={() => { handleItemClicked('Save Button'); onAddEditCalender() }}>
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : "SAVE"}
                        </Button>}
                </DialogActions>
            </Dialog>
        </>
    )
}

export default CalenderCRU